import { useState, useEffect } from "react";
import { Application } from "@/types/application";
import { toast } from "sonner";
import * as appStoreApi from "@/services/appStoreApi";
import * as privateAppsApi from "@/services/privateAppsApi";
import type { AppStoreApplication, ApplicationDTO } from "@/types/api";

/** Map the backend Application entity to the local Application type */
const mapApiToApplication = (app: AppStoreApplication | ApplicationDTO): Application => ({
  id: ('id' in app ? app.id : (app as ApplicationDTO).uuid) || crypto.randomUUID(),
  name: app.name,
  icon: ('iconUrl' in app ? app.iconUrl : undefined) || "",
  description: ('description' in app ? (app as AppStoreApplication).description : undefined) || "",
  version: app.version || "",
  size: app.size || "",
  releaseDate: String(('createdAt' in app ? (app as AppStoreApplication).createdAt : undefined) || ""),
  source: (('applicationType' in app ? (app as AppStoreApplication).applicationType : app.type) === "private"
    ? "private"
    : "public") as Application["source"],
  platform: (app.platform || "iOS") as Application["platform"],
  bundleId: app.bundleId || "",
  categories: ('genres' in app ? (app as AppStoreApplication).genres : undefined) || [],
  customers: [],
  status: "active" as Application["status"],
  appStoreId: app.trackId || undefined,
  minimumOsVersion: app.minimumOsVersion || undefined,
  developer: ('artistName' in app ? (app as any).artistName : undefined) || ('developer' in app ? (app as any).developer : undefined),
  rating: ('averageUserRating' in app ? (app as any).averageUserRating : undefined) || ('rating' in app ? (app as any).rating : undefined),
});

export const useApplications = (customerId?: string) => {
  const [applications, setApplications] = useState<Application[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchApplications = async () => {
    try {
      setLoading(true);
      const response = await privateAppsApi.listPrivateApps();
      const privateData = response.data || [];
      const mapped = privateData.map(mapApiToApplication);
      setApplications(mapped);
      setLoading(false);
    } catch (err) {
      setError(err instanceof Error ? err : new Error("Failed to fetch applications"));
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchApplications();
  }, [customerId]);

  const addApplication = async (app: Omit<Application, "id">): Promise<Application> => {
    try {
      // Build payload matching the backend AppStoreApplication schema
      const payload: AppStoreApplication = {
        id: null as any,
        name: app.name,
        bundleId: app.bundleId || undefined,
        applicationType: "PUBLIC",
        platform: (app.platform === 'macos' ? 'macOS' : 'iOS') as 'iOS' | 'macOS',
        version: app.version,
        size: app.size,
        description: app.description,
        trackId: app.appStoreId || undefined,
        trackViewUrl: app.appStoreUrl || undefined,
        iconUrl: app.icon,
        minimumOsVersion: app.minimumOsVersion,
        artistName: app.developer,
        sellerName: app.developer,
        averageUserRating: app.rating,
        price: app.price,
        formatted_price: app.price === 0 ? 'Free' : `$${app.price}`,
        genres: app.categories,
      };
      const saved = await appStoreApi.savePublicApp(payload);
      const newApp = mapApiToApplication(saved);
      setApplications((prev) => [newApp, ...prev]);
      toast.success("Application added successfully");
      return newApp;
    } catch (error) {
      console.error("Failed to add application:", error);
      toast.error("Failed to add application");
      throw error;
    }
  };

  const updateApplication = async (updatedApp: Application): Promise<Application> => {
    try {
      const payload: AppStoreApplication = {
        id: updatedApp.id,
        name: updatedApp.name,
        bundleId: updatedApp.bundleId || undefined,
        applicationType: "PUBLIC",
        platform: (updatedApp.platform === 'macos' ? 'macOS' : 'iOS') as 'iOS' | 'macOS',
        version: updatedApp.version,
        size: updatedApp.size,
        description: updatedApp.description,
        trackId: updatedApp.appStoreId || undefined,
        iconUrl: updatedApp.icon,
        minimumOsVersion: updatedApp.minimumOsVersion,
      };
      const saved = await appStoreApi.savePublicApp(payload);
      const mapped = mapApiToApplication(saved);
      setApplications((prev) => prev.map((a) => (a.id === updatedApp.id ? mapped : a)));
      toast.success("Application updated successfully");
      return mapped;
    } catch (error) {
      console.error("Failed to update application:", error);
      toast.error("Failed to update application");
      throw error;
    }
  };

  const removeApplication = async (appId: string): Promise<void> => {
    try {
      await appStoreApi.batchDeleteApps([appId]);
      setApplications((prev) => prev.filter((app) => app.id !== appId));
      toast.success("Application deleted");
    } catch (error) {
      console.error("Failed to remove application:", error);
      toast.error("Failed to remove application");
      throw error;
    }
  };

  const getAppStoreCatalog = () => [] as any[];
  const getMacOSCatalog = () => [] as any[];

  return {
    applications,
    loading,
    error,
    addApplication,
    updateApplication,
    removeApplication,
    getAppStoreCatalog,
    getMacOSCatalog,
    refetch: fetchApplications,
  };
};
